import type { TFunction } from 'i18next';
import { getApiUrl } from '@/config/environment';
import { getSession } from '@/core/auth/supabase-auth';
import { triggerBlobDownload } from '@/shared/save/blob';
import type { PreparedHandle, SaveSource } from '@/shared/save/SaveSource';
import { generateInfoFileContent } from '../utils/infoFileGenerator';
import type { InfoFileParams } from '../types/dataUpload.types';

interface BackendFileSaveSourceDeps {
  getFileIds: () => string[];
  hasRows: () => boolean;
  getInfoFileParams: () => Omit<InfoFileParams, 't'>;
  t: TFunction;
}

async function authToken(): Promise<string> {
  const session = await getSession();
  const token = session?.access_token;
  if (!token) throw new Error('No authentication token available. Please log in.');
  return token;
}

export function createBackendFileSaveSource(deps: BackendFileSaveSourceDeps): SaveSource {
  // Cached prepared handle, keyed by the sorted input file-id set (data version).
  let cachedKey: string | null = null;
  let cachedHandle: PreparedHandle | null = null;
  // Every backend id this source has materialised — all cleaned up on leave.
  const ownedIds = new Set<string>();

  return {
    hasData: () => deps.hasRows() && deps.getFileIds().length > 0,

    async prepare(fileName: string): Promise<PreparedHandle | null> {
      const fileIds = deps.getFileIds();
      if (fileIds.length === 0) {
        throw new Error('No processed file ID available. Please try processing again.');
      }
      const key = fileIds.slice().sort().join('|');
      if (cachedKey === key && cachedHandle) return cachedHandle;

      let handle: PreparedHandle;
      if (fileIds.length === 1) {
        handle = { downloadFileId: fileIds[0] };
      } else {
        const token = await authToken();
        const res = await fetch(getApiUrl('/api/loadRowData/merge-and-prepare'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ fileIds, fileName: `${fileName}.csv` }),
        });
        if (!res.ok) throw new Error('Error merging files for download');
        const data = await res.json();
        const id = data.downloadFileId || data.fileId;
        if (!id) throw new Error('Error preparing file');
        handle = { downloadFileId: id };
      }

      cachedKey = key;
      cachedHandle = handle;
      fileIds.forEach(id => ownedIds.add(id));
      ownedIds.add(handle.downloadFileId);
      return handle;
    },

    async download(prepared: PreparedHandle | null, fileName: string): Promise<void> {
      if (!prepared) throw new Error('Error preparing file');
      const token = await authToken();
      const res = await fetch(
        getApiUrl(`/api/loadRowData/download/${prepared.downloadFileId}`),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('Failed to download file');
      const blob = await res.blob();
      triggerBlobDownload(blob, `${fileName}.csv`);
    },

    buildInfoFile(_fileName: string): string {
      return generateInfoFileContent({ ...deps.getInfoFileParams(), t: deps.t });
    },

    async cleanup(): Promise<void> {
      const ids = Array.from(ownedIds);
      if (ids.length === 0) return;
      try {
        const token = await authToken();
        const res = await fetch(getApiUrl('/api/loadRowData/cleanup-files'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ fileIds: ids }),
        });
        if (!res.ok) {
          console.warn('[CLEANUP] Server rejected cleanup:', res.status);
        }
      } catch (e) {
        console.warn('[CLEANUP] Storage cleanup failed:', e);
      } finally {
        ownedIds.clear();
        cachedKey = null;
        cachedHandle = null;
      }
    },
  };
}
